import {
  Box,
  Button,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import { toCurrency } from "./shopUtils";

function StatCard({ icon, label, value, color }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Iconify icon={icon} width={20} sx={{ color: color || "text.secondary" }} />
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
    </Paper>
  );
}

function ReportItemsTable({ items }) {
  if (!Array.isArray(items) || !items.length) return null;

  const totalQty = items.reduce((s, i) => s + Number(i.quantity || 0), 0);
  const totalRev = items.reduce((s, i) => s + Number(i.revenue || 0), 0);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Produkt</TableCell>
              <TableCell>Variante</TableCell>
              <TableCell align="right">Menge</TableCell>
              <TableCell align="right">Umsatz</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow
                key={`${item.productName}-${item.variantLabel}`}
                hover
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {item.productName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {item.variantLabel}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{item.quantity}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {toCurrency(item.revenue)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ "& td": { borderBottom: "none", fontWeight: 600 } }}>
              <TableCell colSpan={2}>
                <Typography variant="body2" fontWeight={700}>
                  Gesamt
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight={700}>
                  {totalQty}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight={700}>
                  {toCurrency(totalRev)}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function ShopReportsTab({
  adminMonth,
  setAdminMonth,
  monthlyReport,
  loadingMonthlyReport,
  onDownloadReport,
  downloadingReport,
}) {
  const hasData = monthlyReport && !loadingMonthlyReport;

  return (
    <Stack spacing={2.5}>
      {/* ── Header + actions ── */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 2.5,
          background:
            "linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(14,165,233,0.08) 100%)",
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6">Monatsbericht</Typography>
            <Typography variant="body2" color="text.secondary">
              Zusammenfassung aller nicht-stornierten Bestellungen im gewählten
              Monat. CSV- und PDF-Export enthalten die gleichen Daten.
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ sm: "center" }}
          >
            <TextField
              type="month"
              label="Monat"
              value={adminMonth}
              onChange={(e) => setAdminMonth(e.target.value)}
              size="small"
              sx={{ width: 200 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => onDownloadReport("csv")}
              disabled={downloadingReport || !hasData}
              startIcon={
                <Iconify icon="mdi:file-delimited-outline" width={18} />
              }
            >
              CSV exportieren
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => onDownloadReport("pdf")}
              disabled={downloadingReport || !hasData}
              startIcon={<Iconify icon="mdi:file-pdf-box" width={18} />}
            >
              PDF exportieren
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ── Content ── */}
      {loadingMonthlyReport ? (
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            {[0, 1, 2].map((i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Skeleton width="50%" height={18} />
                  <Skeleton width="40%" height={32} sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1}>
              <Skeleton width="100%" height={32} />
              <Skeleton width="90%" height={24} />
              <Skeleton width="85%" height={24} />
              <Skeleton width="80%" height={24} />
            </Stack>
          </Paper>
        </Stack>
      ) : monthlyReport ? (
        <Stack spacing={2}>
          {/* Summary stat cards */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon="mdi:receipt-text-outline"
                label="Bestellungen"
                value={monthlyReport.orderCount}
                color="primary.main"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon="mdi:package-variant-closed"
                label="Artikelmenge"
                value={monthlyReport.totalQuantity}
                color="info.main"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon="mdi:currency-eur"
                label="Umsatz"
                value={toCurrency(monthlyReport.totalRevenue)}
                color="success.main"
              />
            </Grid>
          </Grid>

          {/* Items breakdown table */}
          {monthlyReport.items?.length > 0 ? (
            <>
              <Typography variant="subtitle1" fontWeight={600}>
                Artikelaufschlüsselung
              </Typography>
              <ReportItemsTable items={monthlyReport.items} />
            </>
          ) : (
            <Paper
              variant="outlined"
              sx={{ p: 3, textAlign: "center", borderRadius: 2 }}
            >
              <Typography variant="body2" color="text.secondary">
                Keine Artikeldaten für diesen Monat vorhanden.
              </Typography>
            </Paper>
          )}
        </Stack>
      ) : (
        <Paper
          variant="outlined"
          sx={{ p: 5, textAlign: "center", borderRadius: 2 }}
        >
          <Iconify
            icon="mdi:chart-bar"
            width={72}
            sx={{ color: "grey.300", mb: 2 }}
          />
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            Kein Report für den gewählten Monat verfügbar.
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Wähle einen Monat mit Bestellungen aus.
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}
